import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Monitor, LogOut } from "lucide-react";
import { toast } from "sonner";

const device = (ua = "") => {
  const os = /Android/i.test(ua) ? "Android" : /iPhone|iPad/i.test(ua) ? "iOS" : /Windows/i.test(ua) ? "Windows" : /Mac OS/i.test(ua) ? "macOS" : /Linux/i.test(ua) ? "Linux" : "Unknown device";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  return `${br} on ${os}`;
};

export const UserSessionsDialog = ({ target, onClose }) => {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/users/${target.id}/sessions`).then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  useEffect(() => { if (target) { setRows(null); load(); } }, [target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const revokeOne = async (s) => {
    try { await api.delete(`/users/${target.id}/sessions/${s.id}`); toast.success("Session ended"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revokeAll = async () => {
    try { const { data } = await api.post(`/users/${target.id}/sessions/revoke-all`); toast.success(`${target.name} signed out everywhere (${data.revoked})`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-sessions-dialog">
        <DialogHeader>
          <DialogTitle>Active sessions — {target?.name}</DialogTitle>
          <DialogDescription>Ending a session signs that device out on its next action.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {rows === null && <div className="text-sm text-muted-foreground">Loading…</div>}
          {rows?.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="no-sessions">No active sessions.</div>}
          {rows?.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2" data-testid={`session-row-${s.id}`}>
              <div className="flex items-start gap-2 min-w-0">
                <Monitor className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{device(s.user_agent)}{s.is_current && <span className="ml-2 text-[11px] text-primary">(this session)</span>}</div>
                  <div className="text-xs text-muted-foreground">Signed in {fmtDateTime(s.created_at)} · last active {fmtDateTime(s.last_seen)}{s.ip ? ` · ${s.ip}` : ""}</div>
                </div>
              </div>
              <Button size="sm" variant="outline" data-testid={`revoke-session-${s.id}`} onClick={() => revokeOne(s)}>End</Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={!rows?.length} data-testid="revoke-all-sessions-btn" onClick={revokeAll}>
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out everywhere
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
